// PRD-0190/0191 approved blog post view, shared by the Review card (after approval) and the brief
// page the Approved list links to. HelloConvo is where the operator previews the finished post; the
// brand's site agent publishes it elsewhere through the hc CLI or MCP and reports the live URL.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Brief, ReviewApprovalItem } from "../../../../shared/types";
import { useBriefDocument } from "../../hooks/useCampaigns";
import { useBriefVisuals } from "../../hooks/useVisuals";
import { isNotFoundError } from "../../lib/load-state";
import { Markdown } from "../agent/Markdown";
import { CopyButton } from "../ui/CopyButton";
import { usePublishHandoff } from "../../hooks/usePublishHandoff";
import { PublishHandoffPanel } from "./PublishHandoffPanel";
import { ReviewVisualsPanel, thumb } from "./ReviewVisualsPanel";

type BlogBrief = Brief | ReviewApprovalItem;

const BLOG_SURFACES = new Set(["blog", "website"]);
/** States in which an approved blog post can be handed off (or has been). */
const POST_APPROVAL_STATES = new Set<Brief["state"]>(["approved", "scheduled", "published"]);
/** States in which "Ready to publish" may be offered (the server re-checks). */
const READY_STATES = new Set<Brief["state"]>(["approved", "scheduled"]);

/** Blog posts are handed off to the brand's site agent; other surfaces are posted directly. */
export function isBlogBrief(brief: Pick<Brief, "surface" | "platform">): boolean {
  return BLOG_SURFACES.has((brief.surface ?? brief.platform ?? "").toLowerCase());
}

/** An approved (or scheduled / published) blog post: show the preview and the Publish handoff. */
export function isApprovedBlogPost(brief: Pick<Brief, "surface" | "platform" | "state">): boolean {
  return isBlogBrief(brief) && POST_APPROVAL_STATES.has(brief.state);
}

/**
 * The post as it will be published: selected hero image, title, rendered body, and the social (OG)
 * card a shared link shows. Images come from the visual slots (signed Mech Storage URLs).
 */
export function BlogPostPreview({ brief, brandId, content }: { brief: BlogBrief; brandId: string; content: string }) {
  const visualsQ = useBriefVisuals(brandId, brief.id);
  const hero = visualsQ.data?.slots.hero.selected ?? null;
  const og = visualsQ.data?.slots.og.selected ?? null;
  return (
    <div className="space-y-6" data-testid="blog-post-preview">
      <article aria-label="Post preview" className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        {hero ? (
          <img src={thumb(brandId, hero.asset_id, hero.asset?.url)} alt={hero.asset?.alt_text ?? ""} data-testid="preview-hero"
            className="aspect-[2/1] w-full object-cover" />
        ) : (
          <p className="border-b border-dashed border-slate-200 bg-slate-50 px-5 py-6 text-center text-xs text-slate-500" data-testid="preview-hero-missing">
            {visualsQ.isLoading ? "Loading hero image…" : "No hero image selected — choose one under Images."}
          </p>
        )}
        <div className="px-5 py-6 sm:px-8">
          <h2 className="mb-4 text-2xl font-semibold text-slate-900 sm:text-3xl">{brief.title}</h2>
          <Markdown content={content} reading titleAlreadyShown={brief.title} />
        </div>
      </article>
      <figure className="max-w-md space-y-1" data-testid="preview-og">
        <figcaption className="text-xs font-semibold text-slate-700">Social card preview</figcaption>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {og ? (
            <img src={thumb(brandId, og.asset_id, og.asset?.url)} alt={og.asset?.alt_text ?? `Social card for ${brief.title}`}
              className="aspect-[1200/630] w-full object-cover" />
          ) : (
            <p className="aspect-[1200/630] content-center bg-slate-50 text-center text-xs text-slate-500">No social card yet.</p>
          )}
          <p className="border-t border-slate-200 px-3 py-2 text-sm font-medium text-slate-900">{brief.title}</p>
        </div>
      </figure>
    </div>
  );
}

/** Exact `hc publish` commands (packages/hc-cli/src/commands/publish) for this post. */
export function siteAgentCliCommands(brandId: string, briefId: string): string {
  const scope = `--brand ${brandId} --brief ${briefId}`;
  return [
    `hc publish list --brand ${brandId}`,
    `hc publish claim ${scope} --agent <site-agent-name>`,
    `hc publish get ${scope} --out <path/to/post.md> --images-dir .`,
    `hc publish done ${scope} --claim <claim_id> --url <live-url>`,
    `# optional — only if publishing fails (add --kind retry to release the claim instead):`,
    `# hc publish fail ${scope} --claim <claim_id> --reason "<why>"`,
  ].join("\n");
}

/** MCP tools (backend/src/mcp/publish-handoff-tools.ts), in the order a site agent uses them. */
const MCP_TOOLS: Array<{ name: string; args: string; what: string }> = [
  { name: "publish_handoff_list", args: "brand_id", what: "posts ready to publish" },
  { name: "publish_handoff_claim", args: "brand_id, brief_id, agent_id", what: "claim under a lease; keep the claim_id" },
  { name: "publish_handoff_export", args: "brand_id, brief_id, format", what: "the post as a site file + media refs" },
  { name: "publish_handoff_mark_published", args: "brand_id, brief_id, claim_id, published_url", what: "report the live URL" },
  { name: "publish_handoff_mark_failed", args: "brand_id, brief_id, claim_id, reason, kind", what: "optional, only if publishing fails (kind retry releases the claim)" },
];

/** Copyable instructions for the brand's site agent. Needs a token with publishing:handoff. */
export function SiteAgentInstructions({ brandId, briefId }: { brandId: string; briefId: string }) {
  const cli = siteAgentCliCommands(brandId, briefId);
  const mcp = MCP_TOOLS.map((tool) => `${tool.name}(${tool.args})  # ${tool.what}`).join("\n");
  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="site-agent-instructions">
      <p className="text-xs text-slate-600">
        Your site agent publishes this post in the site repo, then reports the live URL here. Its token needs the <code>publishing:handoff</code> scope.
      </p>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-slate-800">CLI</p>
          <CopyButton text={cli} label="Copy CLI commands" />
        </div>
        <pre className="overflow-x-auto whitespace-pre rounded bg-white p-2 text-[11px] leading-5 text-slate-800" data-testid="site-agent-cli">{cli}</pre>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-slate-800">MCP tools</p>
          <CopyButton text={`${mcp}\n\nbrand_id=${brandId} brief_id=${briefId}`} label="Copy MCP tools" />
        </div>
        <ul className="space-y-1 text-[11px] text-slate-700" data-testid="site-agent-mcp">
          {MCP_TOOLS.map((tool) => (
            <li key={tool.name}><code className="font-medium text-slate-900">{tool.name}</code> ({tool.args}) — {tool.what}</li>
          ))}
        </ul>
        <p className="text-[11px] text-slate-500">brand_id <code>{brandId}</code> · brief_id <code>{briefId}</code></p>
      </div>
    </div>
  );
}

interface PanelsProps {
  brief: BlogBrief;
  brandId: string;
  /** Approved (or scheduled) and the draft is known — gates the "Ready to publish" button. */
  canMarkReady: boolean;
}

/** Handoff states in which the site agent is (or may be) working on the post. */
const AGENT_STATES = new Set(["ready", "claimed", "verifying"]);
/** Handoff states after which the operator has to mark the post Ready (again). */
const NEEDS_READY_STATES = new Set(["failed", "withdrawn", "cancelled"]);

/**
 * Images + the Publish handoff. The site-agent CLI/MCP steps appear only while the post is Ready
 * (ready, claimed (an expired claim reads as ready), verifying); before that a one-line hint; once
 * published the status panel shows only the live URL and verification. Callers decide eligibility.
 */
export function BlogPublishPanels({ brief, brandId, canMarkReady }: PanelsProps) {
  // Same query key as PublishHandoffPanel, so this does not add a request.
  const handoff = usePublishHandoff(brandId, brief.id, true).data ?? null;
  const showInstructions = !!handoff && AGENT_STATES.has(handoff.status) && brief.state !== "published";
  const showReadyHint = canMarkReady && brief.state !== "published" && (!handoff || NEEDS_READY_STATES.has(handoff.status));
  return (
    <div className="space-y-3" data-testid="blog-publish-panels">
      {/* PRD-0191: hero + social card; approval and Ready pin the selected images. */}
      <ReviewVisualsPanel brief={brief} brandId={brandId} />
      {/* PRD-0190: the brand's site agent publishes the post and reports the URL. */}
      <section className="space-y-2 rounded-md border border-slate-200 bg-white p-3" aria-label="Publish">
        <h3 className="text-sm font-semibold text-slate-900">Publish</h3>
        <PublishHandoffPanel brief={brief} brandId={brandId} canMarkReady={canMarkReady} />
        {showReadyHint && <p className="text-xs text-slate-600" data-testid="ready-hint">Mark it Ready to publish so your site agent can pick it up.</p>}
        {showInstructions && <SiteAgentInstructions brandId={brandId} briefId={brief.id} />}
      </section>
    </div>
  );
}

interface ApprovedProps {
  brief: Brief;
  brandId: string;
}

/**
 * Brief-page entry point for an approved blog post (the Approved list links here once the post has
 * left the Review queue). Renders nothing for other surfaces or for briefs not yet approved.
 *
 * Changing an image on an approved post revokes approval server-side; the brief refetches as
 * `in_review`. The panels stay mounted for that case so the operator sees why, with a link back to
 * Review to approve again.
 */
export function ApprovedBlogPublishPanels({ brief, brandId }: ApprovedProps) {
  const blog = isBlogBrief(brief);
  const postApproval = isApprovedBlogPost(brief);
  const [sawApproval, setSawApproval] = useState(postApproval);
  useEffect(() => { if (postApproval) setSawApproval(true); }, [postApproval]);
  const backInReview = blog && sawApproval && brief.state === "in_review";

  const draftQ = useBriefDocument(brief.campaign_id, brief.id, "draft", postApproval || backInReview);
  // Only a 404 means "no draft"; any other failure leaves the draft unknown.
  const draftUnknown = draftQ.isLoading || (draftQ.isError && !isNotFoundError(draftQ.error));

  if (!postApproval && !backInReview) return null;
  const canMarkReady = READY_STATES.has(brief.state) && !draftUnknown;

  return (
    <section className="space-y-3 border-t border-slate-200 px-5 py-4" data-testid="approved-blog-publish" aria-label="Publish this post">
      {backInReview && (
        <p role="status" className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="approval-revoked-notice">
          This post went back to review, so it is no longer approved for publishing.{" "}
          <Link to={`/review?brandId=${encodeURIComponent(brandId)}&briefId=${encodeURIComponent(brief.id)}`} className="font-medium underline">
            Approve it again in Review
          </Link>
        </p>
      )}
      <BlogPublishPanels brief={brief} brandId={brandId} canMarkReady={canMarkReady} />
    </section>
  );
}
